/**
 * EmptyState — renders S-3 (no data) and S-4 (filtered empty) states.
 *
 * S-3: "No files yet. Upload your first document to get started." + Upload CTA
 * S-4: "No files match your filters." + "Clear All" link
 */

import React from "react";
import { Box, Button, Link, Typography } from "@mui/material";

interface EmptyStateProps {
  /** Message to display */
  message: string;
  /** Whether this is a filtered-empty state (S-4) vs. no-data state (S-3) */
  isFiltered?: boolean;
  /** Whether to show the Upload CTA button (only for Admin/Team Member) */
  showUploadCta?: boolean;
  /** Callback when Upload CTA is clicked */
  onUpload?: () => void;
  /** Callback when "Clear All" is clicked (only for filtered state) */
  onClearFilters?: () => void;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  message,
  isFiltered = false,
  showUploadCta = false,
  onUpload,
  onClearFilters,
}) => {
  if (isFiltered) {
    return (
      <Box
        data-testid="filtered-empty-state"
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "200px",
          bgcolor: "#fafbfb",
          borderRadius: "8px",
          border: "1px dashed #dae0e2",
          p: 4,
          gap: 2,
        }}
      >
        <Typography sx={{ color: "#898989", textAlign: "center" }}>
          {message}
        </Typography>
        <Link
          data-testid="clear-filters"
          component="button"
          onClick={onClearFilters}
          sx={{ cursor: "pointer", fontSize: "14px" }}
          underline="hover"
        >
          Clear All
        </Link>
      </Box>
    );
  }

  return (
    <Box
      data-testid="empty-state"
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "200px",
        bgcolor: "#fafbfb",
        borderRadius: "8px",
        border: "1px dashed #dae0e2",
        p: 4,
        gap: 2,
      }}
    >
      <Typography sx={{ color: "#898989", textAlign: "center" }}>
        {message}
      </Typography>
      {showUploadCta && onUpload && (
        <Button
          data-testid="upload-cta"
          variant="contained"
          onClick={onUpload}
          sx={{ textTransform: "none" }}
        >
          Upload Files
        </Button>
      )}
    </Box>
  );
};

/**
 * EmailPlaceholder — renders S-6 email placeholder.
 */
export const EmailPlaceholder: React.FC = () => (
  <Box
    data-testid="email-placeholder"
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minHeight: "200px",
      bgcolor: "#fafbfb",
      borderRadius: "8px",
      border: "1px dashed #dae0e2",
    }}
  >
    <Typography sx={{ color: "#898989" }}>
      Email integration coming soon.
    </Typography>
  </Box>
);
